// src/Pages/Load/LoadContainer/modals/NotesChatModal.jsx

"use client";

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FiSend } from "react-icons/fi";
import { TbFileText } from "react-icons/tb";

import { Button } from "../../../../Components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogHeader,
} from "../../../../Components/ui/dialog";

import { decodeJWT } from "../utils/jwt";

export default function NotesChatModal({ isOpen, onClose, selectedLoad }) {
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = decodeJWT(token);
      if (decoded && decoded.user_id) {
        setUserId(decoded.user_id);
      }
    }
  }, [isOpen]);

  const fetchNotes = async () => {
    if (!selectedLoad?.id) return;

    setIsLoading(true);
    try {
      const userToken = localStorage.getItem("token");
      const response = await axios.get(
        `https://new.cajuu.app/api/loadtransport/${selectedLoad.id}/notes`,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Screen-Name": "/Load",
          },
        }
      );

      const notesFromApi = response.data.notes || [];
      const formattedMessages = notesFromApi.map((note) => ({
        id: note.id,
        text: note.message,
        sender: note.author_id === userId ? "me" : "other",
        timestamp: new Date(note.created_at).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        avatar: note.author?.profile_picture_path
          ? `https://new.cajuu.app/storage/${note.author.profile_picture_path}`
          : "https://new.cajuu.app/storage/profile_pictures/profile_picture.png",
        name:
          `${note.author?.first_name || ""} ${note.author?.last_name || ""}`.trim() ||
          "Unknown User",
      }));

      setChatMessages(formattedMessages);
    } catch (error) {
      console.error("Error fetching notes", error);
      toast.error("Failed to load notes");
      setChatMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedLoad?.id) return;

    setIsSending(true);
    try {
      const userToken = localStorage.getItem("token");

      const formData = new FormData();
      formData.append("message", newMessage.trim());

      await axios.post(
        `https://new.cajuu.app/api/loadtransport/${selectedLoad.id}/notes`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "multipart/form-data",
            "Screen-Name": "/Load",
          },
        }
      );

      await fetchNotes();
      setNewMessage("");
      toast.success("Message sent successfully");
    } catch (error) {
      console.error("Error sending note", error);
      toast.error("Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (isOpen && selectedLoad) {
      fetchNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedLoad]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl h-[90vh] sm:h-[700px] flex flex-col p-4 sm:p-6">
        <DialogHeader className="relative">
          <div className="flex justify-between items-start">
            <div>
              <DialogTitle className="flex flex-wrap items-center gap-2 text-base sm:text-lg">
                <TbFileText className="h-5 w-5 flex-shrink-0" />
                <span className="break-words">Notes {selectedLoad?.id}</span>
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm break-words">
                Order: {selectedLoad?.order_id}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-4 sm:space-y-6 min-h-0">
          {isLoading ? (
            <div className="text-center text-gray-500 mt-8 sm:mt-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base">Loading messages...</p>
            </div>
          ) : chatMessages.length === 0 ? (
            <div className="text-center text-gray-500 mt-8 sm:mt-16">
              <TbFileText className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-4 opacity-40" />
              <p className="text-sm sm:text-base">No messages yet</p>
              <p className="text-xs sm:text-sm">Start a conversation about this order</p>
            </div>
          ) : (
            <>
              {chatMessages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start gap-2 sm:gap-3 ${
                    message.sender === "me" ? "flex-row-reverse" : "flex-row"
                  }`}
                >
                  <div
                    className={`flex-shrink-0 ${
                      message.sender === "me" ? "ml-2 sm:ml-3" : "mr-2 sm:mr-3"
                    }`}
                  >
                    <img
                      src={message.avatar}
                      alt={message.name}
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-gray-300"
                      onError={(e) => {
                        e.target.src =
                          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNEOEQ4RDgiLz4KPHBhdGggZD0iTTIwIDIyQzIyLjIwOTEgMjIgMjQgMjAuMjA5MSAyNCAxOEMyNCAxNS43OTA5IDIyLjIwOTEgMTQgMjAgMTRDMTcuNzkwOSAxNCAxNiAxNS43OTA5IDE2IDE4QzE2IDIwLjIwOTEgMTcuNzkwOSAyMiAyMCAyMloiIGZpbGw9IiM5OTk5OTkiLz4KPHBhdGggZD0iTTIwIDI0QzE1LjU4IDI0IDEyIDI2LjI0IDEyIDI5VjMxQzEyIDMxLjU1MjMgMTIuNDQ3NyAzMiAxMyAzMkgyN0MyNy41NTIzIDMyIDI4IDMxLjU1MjMgMjggMzFWMjlDMjggMjYuMjQgMjQuNDIgMjQgMjAgMjRaIiBmaWxsPSIjOTk5OTk5Ii8+Cjwvc3ZnPgo=";
                      }}
                    />
                  </div>

                  <div
                    className={`flex flex-col ${
                      message.sender === "me" ? "items-end" : "items-start"
                    } max-w-[75%] sm:max-w-[70%]`}
                  >
                    <div
                      className={`flex items-center gap-1 sm:gap-2 mb-1 ${
                        message.sender === "me" ? "flex-row-reverse" : "flex-row"
                      }`}
                    >
                      <span className="text-xs font-medium text-white truncate">
                        {message.name}
                      </span>
                      <span className="text-xs text-white whitespace-nowrap">
                        {message.timestamp}
                      </span>
                    </div>

                    <div
                      className={`px-3 py-2 sm:px-4 sm:py-3 rounded-2xl shadow-sm ${
                        message.sender === "me"
                          ? "bg-yellow-600 text-white rounded-br-none"
                          : "bg-gray-100 text-gray-800 border rounded-bl-none"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap leading-relaxed break-words">
                        {message.text}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className="border-t pt-4 flex-shrink-0">
          <div className="flex gap-2 sm:gap-3 flex-col sm:flex-row">
            <div className="flex-1 flex gap-2">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                rows={2}
                className="flex-1 px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600 resize-none text-black"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={isSending}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || isSending}
                className="bg-yellow-600 hover:bg-yellow-700 self-end flex-shrink-0"
                size="sm"
              >
                {isSending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <FiSend className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}